import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GuideTour } from "../../GuideTour/GuideTour";
import { useGuideTour } from "../../GuideTour/useGuideTour";
import {
  CreateWhitelabelGuideShell,
  type CreateWhitelabelGuideShellRef,
} from "./CreateWhitelabelGuideShell";
import {
  GUIDE_WL_BRAND_NAME,
  GUIDE_WL_PACKAGES,
  GUIDE_WL_SKINS,
  GUIDE_WL_SUBDOMAIN,
} from "./fixtures";
import { WL_CREATE_TOUR_STEPS } from "./steps";
import { isWlCreateTourCompleted, WL_CREATE_TOUR_STORAGE_KEY } from "../whitelabelTourStorage";

/** Minimum tour step index expected for a given wizard flow (prevents tour/flow desync). */
function minTourIndexForHostFlow(flow: number): number {
  if (flow <= 0) return 0;
  if (flow === 1) return 4;
  if (flow === 2) return 9;
  return 11;
}

function waitMs(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const t = window.setTimeout(() => {
      if (signal.aborted) reject(new DOMException("Aborted", "AbortError"));
      else resolve();
    }, ms);
    signal.addEventListener(
      "abort",
      () => {
        window.clearTimeout(t);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true }
    );
  });
}

/** Help autopilot pacing (ms) — slower for readability. */
const HELP = {
  intro: 720,
  spotlight: 1100,
  afterType: 850,
  panel: 1350,
  transition: 780,
  branch: 1050,
  major: 1180,
  review: 1480,
  submit: 1550,
} as const;

/** Wizard `flowStep` that matches the whitelabel UI for a given tour spotlight index. */
function hostFlowIndexForTourStep(tourStep: number): number {
  if (tourStep <= 3) return 0;
  if (tourStep <= 8) return 1;
  if (tourStep <= 10) return 2;
  return 3;
}

type TourApi = NonNullable<ReturnType<typeof useGuideTour>>;

async function runHelpSegment(
  segmentIndex: number,
  signal: AbortSignal,
  sh: CreateWhitelabelGuideShellRef,
  T: TourApi,
  setFlowStep: (n: number) => void,
  navigate: (path: string, opts?: { replace?: boolean }) => void,
  fromHelp: boolean
): Promise<void> {
  switch (segmentIndex) {
    case 0:
      T.jumpToStep(0);
      await waitMs(HELP.spotlight, signal);
      await sh.typeBrand(GUIDE_WL_BRAND_NAME);
      await waitMs(HELP.afterType, signal);
      break;
    case 1:
      T.jumpToStep(1);
      await waitMs(HELP.branch, signal);
      await sh.typeSubdomain(GUIDE_WL_SUBDOMAIN);
      await waitMs(HELP.afterType, signal);
      break;
    case 2:
      T.jumpToStep(2);
      await waitMs(HELP.panel, signal);
      break;
    case 3:
      T.jumpToStep(3);
      await waitMs(HELP.branch, signal);
      await sh.pulseControl("#wl-tour-next-branding");
      setFlowStep(1);
      await waitMs(HELP.transition, signal);
      break;
    case 4:
      T.jumpToStep(4);
      await waitMs(HELP.spotlight, signal);
      await sh.pulseControl("#wl-tour-skin");
      sh.setSkinId(GUIDE_WL_SKINS[1]?.skinId ?? GUIDE_WL_SKINS[0].skinId);
      await waitMs(HELP.major, signal);
      break;
    case 5:
      T.jumpToStep(5);
      await waitMs(HELP.branch, signal);
      await sh.pulseControl("#wl-tour-theme-Sunset");
      sh.selectTheme("Classic", "Sunset");
      await waitMs(HELP.major, signal);
      break;
    case 6:
      T.jumpToStep(6);
      await waitMs(HELP.branch, signal);
      await sh.pulseControl("#wl-tour-cat-casino");
      sh.ensureCategory("casino", true);
      await waitMs(HELP.branch, signal);
      break;
    case 7:
      T.jumpToStep(7);
      await waitMs(HELP.spotlight, signal);
      await sh.pulseControl("#wl-tour-payment-modes");
      await waitMs(HELP.spotlight, signal);
      break;
    case 8:
      T.jumpToStep(8);
      await waitMs(HELP.transition, signal);
      await sh.pulseControl("#wl-tour-next-setup");
      setFlowStep(2);
      await waitMs(HELP.transition, signal);
      break;
    case 9:
      T.jumpToStep(9);
      await waitMs(HELP.major, signal);
      await sh.pulseControl("#wl-tour-packages");
      sh.setPackageId(GUIDE_WL_PACKAGES[1]?.packageId ?? GUIDE_WL_PACKAGES[0].packageId);
      await waitMs(HELP.major, signal);
      break;
    case 10:
      T.jumpToStep(10);
      await waitMs(HELP.transition, signal);
      await sh.pulseControl("#wl-tour-next-packages");
      setFlowStep(3);
      await waitMs(HELP.transition, signal);
      break;
    case 11:
      T.jumpToStep(11);
      await waitMs(HELP.review, signal);
      break;
    case 12:
      T.jumpToStep(12);
      await waitMs(HELP.major, signal);
      await sh.pulseControl("#wl-tour-submit");
      await waitMs(HELP.submit, signal);
      T.complete();
      if (fromHelp) {
        navigate("/whitelabel", { replace: true });
      }
      break;
    default:
      break;
  }
}

export default function CreateWhitelabelTourPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromHelp = searchParams.get("from") === "help";

  const [flowStep, setFlowStep] = useState(0);
  const [helpOrchestratorGeneration, setHelpOrchestratorGeneration] = useState(0);
  const shellRef = useRef<CreateWhitelabelGuideShellRef | null>(null);
  const tourRef = useRef<ReturnType<typeof useGuideTour> | null>(null);
  const helpTourBootstrappedRef = useRef(false);
  const orchestratorAbortRef = useRef<AbortController | null>(null);
  const resumeFromStepRef = useRef<number | null>(null);

  const handleHelpExit = useCallback(() => {
    tourRef.current?.skip();
    navigate("/whitelabel", { replace: true });
  }, [navigate]);

  const tour = useGuideTour({
    steps: WL_CREATE_TOUR_STEPS,
    storageKey: WL_CREATE_TOUR_STORAGE_KEY,
    flowStepIndex: flowStep,
    persistCompletion: !fromHelp,
    onEscape: fromHelp ? handleHelpExit : undefined,
  });
  tourRef.current = tour;

  const prevFlowRef = useRef(flowStep);

  useEffect(() => {
    if (!tour.isActive || fromHelp) return;
    if (flowStep > prevFlowRef.current) {
      const minIdx = minTourIndexForHostFlow(flowStep);
      if (tour.stepIndex < minIdx) {
        tour.jumpToStep(minIdx);
      }
    }
    prevFlowRef.current = flowStep;
  }, [flowStep, fromHelp, tour]);

  const autoStartRef = useRef(false);
  useEffect(() => {
    if (fromHelp) {
      if (helpTourBootstrappedRef.current) return;
      helpTourBootstrappedRef.current = true;
      setFlowStep(0);
      resumeFromStepRef.current = null;
      tour.start();
      return () => {
        helpTourBootstrappedRef.current = false;
      };
    }
    helpTourBootstrappedRef.current = false;
    if (autoStartRef.current) return;
    autoStartRef.current = true;
    if (!isWlCreateTourCompleted()) {
      setFlowStep(0);
      tour.start();
    }
    return undefined;
  }, [fromHelp, tour.start]);

  useEffect(() => {
    if (!fromHelp || !tour.isActive) return;

    const ac = new AbortController();
    orchestratorAbortRef.current = ac;
    const signal = ac.signal;

    const run = async () => {
      const waitForShell = async () => {
        for (let i = 0; i < 40; i += 1) {
          if (signal.aborted) throw new DOMException("Aborted", "AbortError");
          if (shellRef.current) return shellRef.current;
          await waitMs(50, signal);
        }
        return shellRef.current;
      };

      try {
        await waitMs(0, signal);
        const sh = await waitForShell();
        if (!sh) return;

        const from = resumeFromStepRef.current ?? 0;
        resumeFromStepRef.current = null;

        if (from === 0) {
          sh.resetDemoFields();
          await waitMs(HELP.intro, signal);
          setFlowStep(0);
        } else {
          setFlowStep(hostFlowIndexForTourStep(from));
          await waitMs(HELP.transition, signal);
        }

        const T = tourRef.current;
        if (!T) return;

        for (let s = from; s <= 12; s += 1) {
          if (signal.aborted) throw new DOMException("Aborted", "AbortError");
          await runHelpSegment(s, signal, sh, T, setFlowStep, navigate, fromHelp);
          if (!tourRef.current?.isActive) return;
        }
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return;
        console.error(e);
      }
    };

    void run();
    return () => {
      ac.abort();
      orchestratorAbortRef.current = null;
    };
  }, [fromHelp, helpOrchestratorGeneration, navigate, tour.isActive]);

  const handleHelpTourPrev = useCallback(() => {
    const t = tourRef.current;
    if (!t || t.stepIndex <= 0) return;
    resumeFromStepRef.current = Math.max(0, t.stepIndex - 1);
    orchestratorAbortRef.current?.abort();
    t.goPrev();
    setHelpOrchestratorGeneration((g) => g + 1);
  }, []);

  const handleShellBack = useCallback(() => {
    if (fromHelp) return;
    if (flowStep <= 0) return;
    if (flowStep === 3) {
      setFlowStep(2);
      tour.jumpToStep(9);
    } else if (flowStep === 2) {
      setFlowStep(1);
      tour.jumpToStep(7);
    } else if (flowStep === 1) {
      setFlowStep(0);
      tour.jumpToStep(2);
    }
  }, [flowStep, fromHelp, tour]);

  const helpAutopilotRunning = fromHelp && tour.isActive;

  return (
    <>
      <CreateWhitelabelGuideShell
        ref={shellRef}
        flowStepIndex={flowStep}
        onFlowStepChange={setFlowStep}
        onRequestBack={handleShellBack}
        blockPointerForAutopilot={helpAutopilotRunning}
      />
      <GuideTour
        isActive={tour.isActive}
        targetRect={tour.targetRect}
        stepIndex={tour.stepIndex}
        totalSteps={tour.totalSteps}
        content={tour.activeStep?.content ?? ""}
        demoKind={tour.activeStep?.demoKind}
        spotlightPadding={tour.activeStep?.spotlightPadding ?? 8}
        autopilotMode={helpAutopilotRunning}
        autopilotShowPrevious={helpAutopilotRunning}
        onNext={tour.goNext}
        onPrev={fromHelp ? handleHelpTourPrev : tour.goPrev}
        onSkip={fromHelp ? handleHelpExit : tour.skip}
      />
    </>
  );
}
